import type { ComponentType } from '@/components/region';
import type { ComponentDesignMetadata, RegionDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { getRegionDefinitions, type RegionDefinitionConfig } from '@/lib/decorators/region-definition';

export function normalizeComponentTypeIds(entries: Array<string | { typeId: string }> | undefined): string[] {
    if (!entries?.length) {
        return [];
    }

    return entries.map((entry) => (typeof entry === 'string' ? entry : entry.typeId));
}

function regionConfigToDesignMetadata(config: RegionDefinitionConfig): RegionDesignMetadata {
    return {
        id: config.id,
        componentTypeInclusions: normalizeComponentTypeIds(config.componentTypeInclusions),
        componentTypeExclusions: normalizeComponentTypeIds(config.componentTypeExclusions),
    };
}

function regionAllowsComponents(metadata?: RegionDesignMetadata): boolean {
    return Boolean(metadata?.componentTypeInclusions && metadata.componentTypeInclusions.length > 0);
}

/**
 * Merges decorator region metadata onto a Page Designer component instance so nested
 * `<Region component={...} />` calls expose allowed child types in design mode.
 */
export function withComponentRegionDesignMetadata(
    component: ComponentType,
    metadataClass: new (...args: never[]) => unknown
): ComponentType {
    const decoratorRegions = getRegionDefinitions(metadataClass).map(regionConfigToDesignMetadata);

    if (decoratorRegions.length === 0) {
        return component;
    }

    const apiRegions = component.designMetadata?.regionDefinitions ?? [];
    const mergedRegions = decoratorRegions.map((decoratorRegion) => {
        const apiRegion = apiRegions.find((region) => region.id === decoratorRegion.id);

        return {
            id: decoratorRegion.id,
            componentTypeInclusions:
                apiRegion && regionAllowsComponents(apiRegion)
                    ? normalizeComponentTypeIds(apiRegion.componentTypeInclusions as Array<string | { typeId: string }>)
                    : (decoratorRegion.componentTypeInclusions ?? []),
            componentTypeExclusions: normalizeComponentTypeIds(
                (apiRegion?.componentTypeExclusions ?? decoratorRegion.componentTypeExclusions) as
                    | Array<string | { typeId: string }>
                    | undefined
            ),
        };
    });

    const designMetadata: ComponentDesignMetadata = {
        id: component.designMetadata?.id ?? component.id,
        contentLinkUuid: component.designMetadata?.contentLinkUuid,
        isFragment: component.designMetadata?.isFragment ?? false,
        isVisible: component.designMetadata?.isVisible ?? true,
        isLocalized: component.designMetadata?.isLocalized ?? false,
        name: component.designMetadata?.name,
        regionDefinitions: mergedRegions,
    };

    return {
        ...component,
        designMetadata,
    };
}
